import { Select } from 'antd5';
import { type Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { None } from 'components/data/helpers';
import { Button, Modal } from 'components/ui';
import {
  type AtlasGqlDamageSchemaAttributeHistoricalString,
  useGetDamageAttributesQuery,
} from 'types/atlas-graphql';

import { type PlotStyleState, ColorCodingAttribute } from '../BladeDamagePlotContainer';

import { StyleOptionContainer } from './PlotStyleOptionsModal.style';

type PlotStyleOptionsModalProps = {
  plotStyleState: PlotStyleState;
  dispatchPlotStyleState: Dispatch<Partial<PlotStyleState>>;
  schemaId?: string;
};

export const PlotStyleOptionsModal: React.FunctionComponent<PlotStyleOptionsModalProps> = ({
  plotStyleState,
  dispatchPlotStyleState,
  schemaId,
}) => {
  const { t } = useTranslation();
  const [workingState, setWorkingState] = useState<PlotStyleState>(plotStyleState);
  useEffect(() => {
    setWorkingState(plotStyleState);
  }, [plotStyleState]);

  const { data } = useGetDamageAttributesQuery({
    variables: { getAllSchemaOptionsId: schemaId },
  });

  const filteredColorCodingAttributes = Object.values(ColorCodingAttribute).filter(
    attr =>
      attr === ColorCodingAttribute.PropagationType ||
      data?.getAllSchemaOptions.some(({ name }) => name === attr)
  );

  return (
    <Modal
      open={plotStyleState.modalOpen}
      onCancel={() => dispatchPlotStyleState({ modalOpen: false })}
      title={t('damage.plotStyleOptions', 'Plot Style Options')}
      footer={
        <div>
          <Button onClick={() => dispatchPlotStyleState({ modalOpen: false })}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="primary"
            onClick={() => dispatchPlotStyleState({ ...workingState, modalOpen: false })}
          >
            {t('common.apply', 'Apply')}
          </Button>
        </div>
      }
    >
      <StyleOptionContainer>
        <div>
          <p>{t('damage.colorCoding', 'Color Coding')}</p>
          <Select
            options={[
              { label: <None />, value: '' },
              ...filteredColorCodingAttributes.map(attribute => ({ value: attribute })),
            ]}
            value={
              workingState.colorCodingAttribute === null ? '' : workingState.colorCodingAttribute
            }
            showSearch={true}
            onChange={(attributeName: ColorCodingAttribute | '') => {
              const attr = data?.getAllSchemaOptions.find(
                attr => 'options' in attr && attr.name === attributeName
              ) as AtlasGqlDamageSchemaAttributeHistoricalString | undefined;

              setWorkingState({
                ...workingState,
                colorCodingAttribute: attributeName === '' ? null : attributeName,
                colorCodingAttributeValues: attr?.options,
              });
            }}
          />
        </div>
      </StyleOptionContainer>
    </Modal>
  );
};
